import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import { auditLogAPI, usersAPI } from '../services/api';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { formatUtcDateTime } from '../utils/dateFormat';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const AUDIT_ACTION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'insert', label: 'Insert' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const ACTION_COLORS = {
  insert: 'success',
  update: 'info',
  delete: 'error',
};

const AdminAuditLogPage = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({
    table_name: '',
    action: '',
    changed_by: '',
    date_from: '',
    date_to: '',
  });

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await auditLogAPI.getAll(params);
      setEntries(toArray(res.data?.items, 'audit log entries'));
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading audit log:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    auditLogAPI.getTables().then((res) => setTables(toArray(res.data, 'audit tables'))).catch(() => {});
    usersAPI.getAll().then((res) => setUsers(toArray(res.data, 'users'))).catch(() => {});
  }, []);

  const handleFilterChange = (field) => (e) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const username = (id) => users.find((u) => u.id === id)?.username || `#${id}`;

  const tableFilterOptions = useMemo(() => ['', ...tables], [tables]);

  const userFilterOptions = useMemo(() => [
    { value: '', label: 'All' },
    ...users.map((u) => ({ value: String(u.id), label: u.username })),
  ], [users]);

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Typography variant="h4" sx={{ mb: 2 }}>Audit Log</Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
          <Autocomplete
            size="small"
            disableClearable
            sx={{ minWidth: 160 }}
            options={tableFilterOptions}
            getOptionLabel={(t) => t || 'All'}
            filterOptions={createPrefixFilter((t) => t || 'All')}
            value={filters.table_name}
            onChange={(e, newValue) => {
              setPage(0);
              setFilters((prev) => ({ ...prev, table_name: newValue || '' }));
            }}
            renderInput={(params) => <TextField {...params} label="Table" />}
          />

          <Autocomplete
            size="small"
            disableClearable
            sx={{ minWidth: 140 }}
            options={AUDIT_ACTION_OPTIONS}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v?.value}
            filterOptions={createPrefixFilter((o) => o.label)}
            value={AUDIT_ACTION_OPTIONS.find((o) => o.value === filters.action) ?? AUDIT_ACTION_OPTIONS[0]}
            onChange={(e, newValue) => {
              setPage(0);
              setFilters((prev) => ({ ...prev, action: newValue?.value ?? '' }));
            }}
            renderInput={(params) => <TextField {...params} label="Action" />}
          />

          <Autocomplete
            size="small"
            disableClearable
            sx={{ minWidth: 160 }}
            options={userFilterOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v?.value}
            filterOptions={createPrefixFilter((o) => o.label)}
            value={userFilterOptions.find((o) => o.value === filters.changed_by) ?? userFilterOptions[0]}
            onChange={(e, newValue) => {
              setPage(0);
              setFilters((prev) => ({ ...prev, changed_by: newValue?.value ?? '' }));
            }}
            renderInput={(params) => <TextField {...params} label="User" />}
          />

          <TextField
            size="small"
            label="From"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.date_from}
            onChange={handleFilterChange('date_from')}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.date_to}
            onChange={handleFilterChange('date_to')}
          />
        </Box>
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Record ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={5} />}
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  onClick={() => setSelectedEntry(entry)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{formatUtcDateTime(entry.changed_at)}</TableCell>
                  <TableCell>{entry.changed_by_username || (entry.changed_by ? username(entry.changed_by) : 'System')}</TableCell>
                  <TableCell>{entry.table_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.action}
                      size="small"
                      color={ACTION_COLORS[entry.action] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{entry.record_id}</TableCell>
                </TableRow>
              ))}
              {!loading && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No audit log entries found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />

      <Dialog open={Boolean(selectedEntry)} onClose={() => setSelectedEntry(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEntry && `${selectedEntry.table_name} #${selectedEntry.record_id} — ${selectedEntry.action}`}
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Old</TableCell>
                <TableCell>New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedEntry?.changes || []).map((change, idx) => (
                <TableRow key={idx}>
                  <TableCell>{change.field}</TableCell>
                  <TableCell>{change.old === null || change.old === undefined ? '—' : String(change.old)}</TableCell>
                  <TableCell>{change.new === null || change.new === undefined ? '—' : String(change.new)}</TableCell>
                </TableRow>
              ))}
              {(!selectedEntry?.changes || selectedEntry.changes.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No field changes recorded.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEntry(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAuditLogPage;
